import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { formatError } from "../lib/api";
import { toast } from "sonner";

const COUNTRIES = [
  { code: "FR", label_fr: "France", label_en: "France" },
  { code: "BE", label_fr: "Belgique", label_en: "Belgium" },
  { code: "CH", label_fr: "Suisse", label_en: "Switzerland" },
  { code: "CA", label_fr: "Canada", label_en: "Canada" },
  { code: "US", label_fr: "États-Unis", label_en: "United States" },
  { code: "GB", label_fr: "Royaume-Uni", label_en: "United Kingdom" },
  { code: "DE", label_fr: "Allemagne", label_en: "Germany" },
  { code: "ES", label_fr: "Espagne", label_en: "Spain" },
  { code: "IT", label_fr: "Italie", label_en: "Italy" },
  { code: "OTHER", label_fr: "Autre pays", label_en: "Other country" },
];

export default function Register() {
  const { register } = useAuth();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    country: "FR",
    website: "", // honeypot: humans leave this empty
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const passwordStrong =
    form.password.length >= 10 && /\d/.test(form.password);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!passwordStrong) {
      setError(
        lang === "fr"
          ? "Le mot de passe doit contenir au moins 10 caractères et au moins un chiffre."
          : "Password must be at least 10 characters and contain at least one digit."
      );
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await register({
        ...form,
        email: form.email.trim().toLowerCase(),
        language: lang,
      });
      toast.success("Compte créé !");
      navigate("/onboarding");
    } catch (err) {
      setError(formatError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-xl px-6 py-16" data-testid="register-page">
      <h1 className="font-serif text-4xl font-semibold text-[#101A42] sm:text-5xl">
        {t("register")}
      </h1>
      <p className="mt-3 text-lg text-[#4A4A4A]">{t("app_tagline")}</p>

      <form onSubmit={onSubmit} className="mt-10 space-y-6">
        <div>
          <Label htmlFor="name" className="mb-2 block text-lg font-medium">
            {t("name")}
          </Label>
          <Input
            id="name"
            data-testid="register-name-input"
            required
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            className="h-16 rounded-xl border-2 text-lg"
          />
        </div>

        <div>
          <Label htmlFor="email" className="mb-2 block text-lg font-medium">
            {t("email")}
          </Label>
          <Input
            id="email"
            data-testid="register-email-input"
            type="email"
            autoComplete="email"
            required
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
            className="h-16 rounded-xl border-2 text-lg"
          />
        </div>

        <div>
          <Label htmlFor="password" className="mb-2 block text-lg font-medium">
            {t("password")}
          </Label>
          <Input
            id="password"
            data-testid="register-password-input"
            type="password"
            autoComplete="new-password"
            required
            minLength={10}
            value={form.password}
            onChange={(e) => set("password", e.target.value)}
            className="h-16 rounded-xl border-2 text-lg"
          />
          <p
            className={`mt-2 text-sm ${
              form.password.length === 0
                ? "text-[#4A4A4A]"
                : passwordStrong
                ? "text-[#A762B1]"
                : "text-[#A762B1]"
            }`}
            data-testid="register-password-hint"
          >
            {form.password.length === 0
              ? lang === "fr"
                ? "Au moins 10 caractères, avec au moins un chiffre."
                : "At least 10 characters, with at least one digit."
              : passwordStrong
              ? lang === "fr"
                ? "✓ Mot de passe assez fort."
                : "✓ Password is strong enough."
              : lang === "fr"
              ? "Il manque des caractères ou un chiffre."
              : "Missing characters or a digit."}
          </p>
        </div>

        {/* Honeypot: hidden field that only bots will fill. Not shown to humans, screen readers ignore it. */}
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            left: "-9999px",
            top: "-9999px",
            width: "1px",
            height: "1px",
            overflow: "hidden",
          }}
        >
          <label htmlFor="website">Website</label>
          <input
            id="website"
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            value={form.website}
            onChange={(e) => set("website", e.target.value)}
          />
        </div>

        <div>
          <Label className="mb-2 block text-lg font-medium">{t("country")}</Label>
          <Select value={form.country} onValueChange={(v) => set("country", v)}>
            <SelectTrigger
              data-testid="register-country-select"
              className="h-16 rounded-xl border-2 bg-white text-lg"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white">
              {COUNTRIES.map((c) => (
                <SelectItem key={c.code} value={c.code} className="text-lg">
                  {lang === "fr" ? c.label_fr : c.label_en}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {error && (
          <div
            data-testid="register-error"
            className="rounded-xl border-2 border-[#A762B1]/30 bg-[#A762B1]/10 p-4 text-[#6E3978]"
          >
            {error}
          </div>
        )}

        <p
          className="text-center text-sm leading-relaxed text-[#4A4A4A]"
          data-testid="register-consent-notice"
        >
          {lang === "fr" ? (
            <>
              En créant un compte, j'accepte les{" "}
              <Link
                to="/cgu"
                data-testid="register-terms-link"
                className="font-semibold text-[#101A42] underline underline-offset-4"
              >
                Conditions Générales d'Utilisation
              </Link>{" "}
              et la{" "}
              <Link
                to="/confidentialite"
                data-testid="register-privacy-link"
                className="font-semibold text-[#101A42] underline underline-offset-4"
              >
                Politique de confidentialité
              </Link>{" "}
              de Sereno.
            </>
          ) : (
            <>
              By creating an account, I accept Sereno's{" "}
              <Link
                to="/cgu"
                data-testid="register-terms-link"
                className="font-semibold text-[#101A42] underline underline-offset-4"
              >
                Terms of Use
              </Link>{" "}
              and{" "}
              <Link
                to="/confidentialite"
                data-testid="register-privacy-link"
                className="font-semibold text-[#101A42] underline underline-offset-4"
              >
                Privacy Policy
              </Link>
              .
            </>
          )}
        </p>

        <Button
          type="submit"
          disabled={submitting}
          data-testid="register-submit-btn"
          className="min-h-[64px] w-full rounded-full bg-[#A762B1] text-xl font-semibold text-white hover:bg-[#B775C0]"
        >
          {submitting ? "..." : t("signup_cta")}
        </Button>

        <p className="text-center text-lg text-[#4A4A4A]">
          {t("have_account")}{" "}
          <Link
            to="/login"
            data-testid="goto-login-link"
            className="font-semibold text-[#101A42] underline underline-offset-4"
          >
            {t("login")}
          </Link>
        </p>
      </form>
    </div>
  );
}
